/**
 * Single source of truth for the driver-table keyboard shortcuts.
 *
 * The same registry both binds the shortcuts (see `useDriverShortcuts`) and renders the shortcuts pane (see
 * `KeyboardShortcutsDialog`), so the documented bindings can never drift from the ones actually wired up.
 */

/** Stable identifier for each shortcut, used to look up its label/keys when emitting analytics. */
export type DriverShortcutId =
  | "focusSearch"
  | "toggleChannel"
  | "openComposer"
  | "toggleSelect"
  | "extendSelectionDown"
  | "extendSelectionUp"
  | "toggleUnread"
  | "fleetMessage"
  | "markRead"
  | "moveDown"
  | "moveUp"
  | "showShortcuts"
  | "escape"
  | "globalSearch";

export type DriverShortcutCategory = "Navigation" | "Rows" | "Actions" | "Help";

export interface DriverShortcut {
  id: DriverShortcutId;
  /**
   * `react-hotkeys-hook` key strings this shortcut binds to. Omitted for entries that are documented in the pane but
   * bound elsewhere (the app-wide Cmd/Ctrl+K search).
   */
  hotkeys?: readonly string[];
  /** Human-readable keycaps shown in the pane. Each inner array is one keycap group ("or"-separated). */
  displayKeys: readonly (readonly string[])[];
  label: string;
  category: DriverShortcutCategory;
}

const IS_APPLE =
  typeof navigator !== "undefined" && /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);

/** The modifier keycap shown for Cmd/Ctrl+K, matched to the current platform. */
export const PRIMARY_MODIFIER_LABEL = IS_APPLE ? "⌘" : "Ctrl";

export const DRIVER_SHORTCUTS = {
  moveDown: {
    id: "moveDown",
    hotkeys: ["down", "j"],
    displayKeys: [["↓"], ["J"]],
    label: "Highlight next driver",
    category: "Navigation",
  },
  moveUp: {
    id: "moveUp",
    hotkeys: ["up", "k"],
    displayKeys: [["↑"], ["K"]],
    label: "Highlight previous driver",
    category: "Navigation",
  },
  focusSearch: {
    id: "focusSearch",
    hotkeys: ["/", "s"],
    displayKeys: [["/"], ["S"]],
    label: "Search drivers",
    category: "Navigation",
  },
  globalSearch: {
    id: "globalSearch",
    displayKeys: [[PRIMARY_MODIFIER_LABEL, "K"]],
    label: "Open global search",
    category: "Navigation",
  },
  toggleChannel: {
    id: "toggleChannel",
    hotkeys: ["enter", "o"],
    displayKeys: [["Enter"], ["O"]],
    label: "Open or close the highlighted driver's channel",
    category: "Navigation",
  },
  openComposer: {
    id: "openComposer",
    hotkeys: ["m"],
    displayKeys: [["M"]],
    label: "Open channel and start typing a message",
    category: "Navigation",
  },
  toggleSelect: {
    id: "toggleSelect",
    hotkeys: ["x", "shift+x"],
    displayKeys: [["X"]],
    label: "Check the box for the highlighted driver",
    category: "Rows",
  },
  extendSelectionDown: {
    id: "extendSelectionDown",
    hotkeys: ["shift+down", "shift+j"],
    displayKeys: [["⇧", "↓"]],
    label: "Highlight a range of drivers downward",
    category: "Rows",
  },
  extendSelectionUp: {
    id: "extendSelectionUp",
    hotkeys: ["shift+up", "shift+k"],
    displayKeys: [["⇧", "↑"]],
    label: "Highlight a range of drivers upward",
    category: "Rows",
  },
  toggleUnread: {
    id: "toggleUnread",
    hotkeys: ["u"],
    displayKeys: [["U"]],
    label: "Toggle the unread-only filter",
    category: "Actions",
  },
  fleetMessage: {
    id: "fleetMessage",
    hotkeys: ["f"],
    displayKeys: [["F"]],
    label: "New fleet message to selected drivers",
    category: "Actions",
  },
  markRead: {
    id: "markRead",
    hotkeys: ["r"],
    displayKeys: [["R"]],
    label: "Mark the highlighted driver as read",
    category: "Actions",
  },
  showShortcuts: {
    id: "showShortcuts",
    // Matched by produced character (`useKey`), so a plain "?" entry handles Shift+/ across layouts.
    hotkeys: ["?"],
    displayKeys: [["?"]],
    label: "Show keyboard shortcuts",
    category: "Help",
  },
  escape: {
    id: "escape",
    hotkeys: ["escape"],
    displayKeys: [["Esc"]],
    label: "Close driver channel",
    category: "Help",
  },
} as const satisfies Record<DriverShortcutId, DriverShortcut>;

/** Ordered list backing the shortcuts pane; grouped by category in render order. */
export const DRIVER_SHORTCUT_LIST: readonly DriverShortcut[] = [
  DRIVER_SHORTCUTS.moveDown,
  DRIVER_SHORTCUTS.moveUp,
  DRIVER_SHORTCUTS.focusSearch,
  DRIVER_SHORTCUTS.globalSearch,
  DRIVER_SHORTCUTS.toggleChannel,
  DRIVER_SHORTCUTS.openComposer,
  DRIVER_SHORTCUTS.toggleSelect,
  DRIVER_SHORTCUTS.extendSelectionDown,
  DRIVER_SHORTCUTS.extendSelectionUp,
  DRIVER_SHORTCUTS.toggleUnread,
  DRIVER_SHORTCUTS.fleetMessage,
  DRIVER_SHORTCUTS.markRead,
  DRIVER_SHORTCUTS.showShortcuts,
  DRIVER_SHORTCUTS.escape,
];

export const DRIVER_SHORTCUT_CATEGORY_ORDER: readonly DriverShortcutCategory[] = [
  "Navigation",
  "Rows",
  "Actions",
  "Help",
];

/**
 * Computes the next roving-highlight index when moving by `delta` rows.
 *
 * With nothing highlighted, moving down lands on the first row and moving up on the last. Otherwise the index is
 * clamped within the list so the highlight never leaves the rendered rows. Returns `null` for an empty list.
 *
 * @param currentIndex - Index of the currently highlighted row, or `-1` when nothing is highlighted.
 * @param delta - `1` to move down, `-1` to move up.
 * @param rowCount - Number of rows currently in the table.
 * @returns The next index to highlight, or `null` when there are no rows.
 */
export function nextHighlightIndex(currentIndex: number, delta: 1 | -1, rowCount: number): number | null {
  if (rowCount <= 0) return null;
  if (currentIndex < 0) return delta > 0 ? 0 : rowCount - 1;
  return Math.min(rowCount - 1, Math.max(0, currentIndex + delta));
}
